import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, ButtonGroup, Paper, Stack, Step, StepButton, StepContent, Stepper, Tooltip, Typography, IconButton, Dialog, DialogTitle, DialogContent, TextField, Divider, Box } from "@mui/material";
import { ArrowBack, Lock, Edit, Delete, FileDownload, SsidChart, Attachment, Timeline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import ProjectSummary from "./PreparationPhase/Step_1_ProjectSummary";

import ProjectAdvice from "./DesignPhase/Step_1_Advice/Advice";
import DesignScoreTabs from "./DesignPhase/Step_2_Designs/DesignScoreTabs";
import CompareTabs from "./DesignPhase/Step_3_Compare/CompareTabs";
import DesignDiscussion from "./DesignPhase/Step_4_Discuss/DesignDiscussion";
import LoopManagement from "./DesignPhase/Step_0_Setup/LoopManagement";

import Error from "../../pages/ErrorPage";
import ConfirmDeleteDialog from "../ConfirmDeleteDialog";

import { advanceStatus, renamePhase, renameLoop, deleteLastLoop } from "./ProjectActions";
import SegmentsSelect from "./DesignPhase/SegmentsSelect";
import { romanNumbers, letterNumbering } from "../../context/global";
import StartDesignPhase from "./PreparationPhase/Step_3_StartDesignPhase";
import { setCurrentLoopAction } from "../../store/actions/loopActions";
import AttachmentOverview from "../Attachments/AttachmentOverview";
import VoortgangPage from "../../pages/VoortgangPage";
import ExportDialog from "./Export/ExportDialog";
import AdditionalProjectInfo from "./PreparationPhase/Step_2_AdditionalInfo";

export default function ProjectMain({ project }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [segmentDialogOpen, setSegmentDialogOpen] = useState(false);
    const [preferredDesign, setpreferredDesign] = useState(undefined);
    const preparationSteps = ["Algemeen", "Aanvullende projectinformatie", "Ontwerpproces starten"];
    const explorationSteps = ["Setup", "Adviseren", "Toetsen", "Vergelijken", "Concluderen"];
    const [renameDialog, setRenameDialog] = useState({ open: false, type: null, currentName: '', newName: '', id: null });
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);

    const steps = [
        { label: "Opstart", loops: [{ label: "", steps: preparationSteps }], steps: preparationSteps },
        ...project.phases.map((phase) => ({
            label: phase.phase_name,
            loops: phase.loops.map((loop, loopIndex) => ({ ...loop, steps: explorationSteps, label: loop.name || `Ontwerpronde ${loopIndex + 1}` })),
            steps: explorationSteps,
        })),
    ];
    const getLastLoopID = (project) => {
        const lastPhase = project?.phases.slice(-1)[0];
        const id = lastPhase?.loops?.slice(-1)[0]?.id;
        if (id !== undefined) {
            return id;
        }
        return null;
    }
    const getPreviousLoopID = (project) => {
        const phases = project.phases   
        const lastPhase = phases[phases.length - 1]

        if (lastPhase.loops.length > 1) {
            return lastPhase.loops.slice(-2)[0].id;
        }
        if (phases.length > 1) {
            return phases[phases.length - 2].loops.slice(-1)[0].id;
        }
        return null;
    }

    const [activeStep, setActiveStep] = useState({ phase: 0, loop: 0, step: 0 });

    useEffect(() => {
        const currentPhase = project.phases.length;
        const currentLoop = currentPhase === 0 ? 0 : project.phases[currentPhase - 1].loops.length - 1;
        if (activeStep.phase !== 0) {
            setActiveStep({ phase: currentPhase, loop: currentLoop, step: 0 });
        }
    }, [project.phases.length, project?.phases[project.phases.length - 1]?.loops?.length]);

    const handleNextStep = () => {
        if (activeStep.step < steps[activeStep.phase].steps.length - 1) {
            setActiveStep({ ...activeStep, step: activeStep.step + 1 });
        } else if (activeStep.loop < steps[activeStep.phase].loops.length - 1) {
            setActiveStep({ ...activeStep, step: 0, loop: Math.min(activeStep.loop + 1, steps[activeStep.loop].steps.length - 1) });
        } else {
            setActiveStep({ step: 0, loop: 0, phase: Math.min(activeStep.phase + 1, project.phases.length) });
        }
    };
    const handlePreviousStep = () => {
        if (activeStep.step > 0) {
            setActiveStep({ ...activeStep, step: activeStep.step - 1 });
        } else if (activeStep.loop > 0) {
            setActiveStep({
                ...activeStep,
                loop: Math.max(activeStep.loop - 1, 0),
                step: 0,
            });
        } else {
            setActiveStep({
                phase: Math.max(activeStep.phase - 1, 0),
                loop: Math.max(activeStep.loop - 1, 0),
                step: Math.max(steps[activeStep.loop].steps.length - 1, 0),
            });
        }
    };
    const handleStep = (step) => () => {
        setActiveStep(step);
    };
    const handleAdvancePhase = (design) => {
        if (design) {
            setpreferredDesign(design);
        }
        setSegmentDialogOpen(true);
    };

    const handleRenamePhase = (phaseId, currentName) => {
        setRenameDialog({
            open: true,
            type: 'phase',
            currentName: currentName,
            newName: currentName,
            id: phaseId
        });
    };

    const handleRenameLoop = (loopId, currentName) => {
        setRenameDialog({
            open: true,
            type: 'loop',
            currentName: currentName,
            newName: currentName,
            id: loopId
        });
    };

    const handleSaveRename = async () => {
        try {
            if (renameDialog.type === 'phase') {
                await renamePhase(project.id, renameDialog.id, renameDialog.newName, dispatch);
            } else if (renameDialog.type === 'loop') {
                await renameLoop(project.id, renameDialog.id, renameDialog.newName, dispatch);
            }
            setRenameDialog({ open: false, type: null, currentName: '', newName: '', id: null });
        } catch (error) {
            console.error('Rename error:', error);
            // Dialog stays open so user can try again
        }
    };

    const handleDeleteLastLoop = async () => {
        const previousLoopID = getPreviousLoopID(project);
        const success = await deleteLastLoop(project.id, dispatch);
        if (success) {
            setDeleteConfirmDialog(false);
            setCurrentLoopAction(previousLoopID);
        }
    };

    // Check if there are any design loops to delete
    const hasDesignLoops = () => {
        return project.phases && project.phases.length > 0 && 
               project.phases.some(phase => phase.loops && phase.loops.length > 0);
    };


    const defaultPhaseName = (index) => {
        switch (index) {
            case 0:
                return "Opstart";
            case 1:
                return "Verkenningsfase";
            case 2:
                return "Planuitwerkingsfase";
            default:
                return "Fase" + index;
        }
    };
    const ProjectStepper = ({ step, setStep }) => {
        return (
            // Map the phases
            <Stepper nonLinear activeStep={step.phase} orientation="vertical" >
                {steps.map((phase, phaseIndex) => (
                    <Step key={phaseIndex}>
                        <StepButton onClick={setStep({ phase: phaseIndex, loop: 0, step: 0 })} sx={{ flex: 1 }}>
                            {phase.label || defaultPhaseName(phaseIndex)} {phaseIndex > 0 && (project.user_is_owner || project.user_is_orgadmin) && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const currentPhase = project.phases[phaseIndex - 1];
                                    if (currentPhase) {
                                        handleRenamePhase(currentPhase.id, currentPhase.phase_name);
                                    }
                                }}
                                title="Fase hernoemen"
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        )}
                        </StepButton>
                        {/* Edit button for phases (only for design phases) */}
                            
                        <StepContent>
                            {/* Map the Loops */}
                            <Stepper nonLinear activeStep={step.loop} orientation="vertical">
                                {phase.loops &&
                                    phase.loops.map((loop, loopIndex) => (
                                        <Step key={loopIndex}>
                                            {phaseIndex > 0 && (
                                                <StepButton
                                                    icon={romanNumbers[loopIndex]}
                                                    onClick={setStep({
                                                        phase: phaseIndex,
                                                        loop: loopIndex,
                                                        step: 0,
                                                    })}
                                                    sx={{ flex: 1 }}
                                                >
                                                    {loop.label} {(project.user_is_owner || project.user_is_orgadmin) && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                                                                                            const currentPhase = project.phases[phaseIndex - 1];
                                                                const currentLoop = currentPhase?.loops[loopIndex];
                                                                if (currentLoop) {
                                                                    const currentLoopName = currentLoop.name || `Ontwerploop ${loopIndex + 1}`;
                                                                    handleRenameLoop(currentLoop.id, currentLoopName);
                                                                }
                                                        }}
                                                        title="Naam wijzigen"
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                )}
                                                </StepButton>

                                                   
                                            )}
                                            <StepContent>
                                                <Stepper nonLinear activeStep={step.step} orientation="vertical">
                                                    {loop.steps.map((step, stepIndex) => (
                                                        <Step
                                                            key={stepIndex}
                                                            completed={
                                                                phaseIndex === 0
                                                                    ? stepIndex === 4
                                                                        ? !!project.reference_design
                                                                        : project.status > stepIndex
                                                                    : false
                                                            }
                                                        >
                                                            <StepButton
                                                                icon={letterNumbering[stepIndex]}
                                                                onClick={setStep({
                                                                    phase: phaseIndex,
                                                                    loop: loopIndex,
                                                                    step: stepIndex,
                                                                })}
                                                            >
                                                                {step}
                                                            </StepButton>
                                                        </Step>
                                                    ))}
                                                </Stepper>
                                            </StepContent>
                                        </Step>
                                    ))}
                                    
                            </Stepper>
                        </StepContent>
                    </Step >
                ))
                }
            </Stepper >
        );
    };

    const lockAndUnlockPage = (activeStep) => {
        if (activeStep.step > 0 && activeStep.step < 4) {
            if ((activeStep.phase === 0 && project.status > activeStep.step) || project.phases.length > activeStep.phase) {
                return (project.user_is_orgadmin || project.user_is_owner) ? (
                    <Tooltip
                        title={
                            "Ontgrendel informatie in dit project vanaf dit punt. Let op: dit kan complicaties veroorzaken in latere stappen van dit project!"
                        }
                    >
                        <Button onClick={() => advanceStatus(project.id, { status: activeStep.step - 1 }, dispatch)}>
                            <Lock />
                            Ontgrendelen
                        </Button>
                    </Tooltip>
                ) : (
                    <Tooltip title={"Neem contact op met een project eigenaar om informatie te ontgrendelen "}>
                        <Lock />
                    </Tooltip>
                );
            }
        }
    };

    const renderSwitch = (step) => {
        const payload = {
            project: project,
            activeStep: activeStep,
            handlePreviousStep: handlePreviousStep,
            steps: steps,
            handleNextStep: handleNextStep,
            editable: project.user_is_owner || project.user_is_orgadmin,
            phaseIndex: step.phase - 1,
            phase: project.phases[step.phase - 1],
            lastLoopID: getLastLoopID(project),
        };
        if (step.phase === 0) {
            // Preparation phase
            switch (step.step) {
                case 0:
                    return <ProjectSummary payload={payload} />;
                case 1:
                    return <AdditionalProjectInfo payload={payload}/>    
                case 2:
                    return <StartDesignPhase payload={payload} handleAdvancePhase={handleAdvancePhase} />;
                case "bijlagen":
                    return <AttachmentOverview payload={payload} />;
                case "voortgang":
                    return <VoortgangPage payload={payload} />;
                default:
                    return <Error />;
            }
        }
        else {
            //Exploration/design phase
            payload.loop = project.phases[step.phase - 1].loops[step.loop];
            payload.loopIndex = step.loop;
            const currentLoopIsLastLoop =
                step.phase === project.phases.length &&
                step.loop === ((project.phases[step.phase - 1]?.loops?.length || 1) - 1);
            payload.currentLoopIsLastLoop = currentLoopIsLastLoop;
            payload.editable = payload.editable && payload.currentLoopIsLastLoop;

            let content = null;
            switch (step.step) {
                case 0:
                    content = (
                        <LoopManagement
                            loopId={payload.loop?.id}
                            editable={payload.editable && payload.currentLoopIsLastLoop}
                            currentLoopIsLastLoop={payload.currentLoopIsLastLoop}
                            payload={payload}
                        />
                    );
                    break;
                case 1:
                    content = <ProjectAdvice payload={payload} />;
                    break;
                case 2:
                    content = <DesignScoreTabs payload={payload} />;
                    break;
                case 3:
                    content = <CompareTabs payload={payload} />;
                    break;
                case 4:
                    content = (
                        <DesignDiscussion
                            payload={payload}
                            handleAdvancePhase={handleAdvancePhase}
                        />
                    );
                    break;
                default:
                    content = <Error />;
            }

            return (
                <>
                    {!currentLoopIsLastLoop && (
                        <Alert severity="warning" sx={{ position: 'sticky', top: 80, zIndex: 50 }}>
                            Dit is een niet-actieve ontwerpronde. Je kunt hier niet meer aanpassen.
                        </Alert>
                    )}
                    {content}
                </>
            );
        }
    };

    const currentLoopIsLastLoop = (step) => {
        return step.phase === project.phases.length &&
            step.loop === ((project.phases[step.phase - 1]?.loops?.length || 1) - 1);
    };

    return (
        <>
            <ExportDialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} />
            <Dialog open={renameDialog.open} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {renameDialog.type === 'phase' ? 'Fase hernoemen' : 'Ontwerpronde hernoemen'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nieuwe naam"
                        fullWidth
                        variant="outlined"
                        value={renameDialog.newName}
                        onChange={(e) => setRenameDialog({ ...renameDialog, newName: e.target.value })}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <ButtonGroup sx={{ marginLeft: "auto", marginRight: "auto", marginBottom: 2 }}>
                    <Button onClick={() => setRenameDialog({ open: false, type: null, currentName: '', newName: '', id: null })}>
                        Annuleren
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveRename}
                        disabled={!renameDialog.newName.trim()}
                    >
                        Opslaan
                    </Button>
                </ButtonGroup>
            </Dialog>

            <ConfirmDeleteDialog
                open={deleteConfirmDialog}
                onClose={() => setDeleteConfirmDialog(false)}
                dialogTitle="Laatste ontwerpronde verwijderen"
                dialogContentText="Weet je zeker dat je de laatste ontwerpronde wilt verwijderen? Als dit de enige ronde in de fase is, wordt de hele fase ook verwijderd."
                handleConfirm={handleDeleteLastLoop}
                dialogConfirmButton="Verwijderen"
                critical={false}
            />

            <SegmentsSelect
                project={project}
                isFirstPhase={project.phases.length === 0}
                design={preferredDesign}
                open={segmentDialogOpen}
                onClose={() => setSegmentDialogOpen(false)}
            />
            {steps[activeStep.phase]?.steps[activeStep?.step] !== undefined || activeStep.step === "bijlagen" || activeStep.step === "voortgang" ? (
                <Stack direction="row" spacing={2} width={"100%"} minWidth={"min-content"}>
                    <Stack component={Paper}>
                        <ButtonGroup>
                            <Button variant="outlined" onClick={() => navigate("/projects")}>
                                <ArrowBack />
                                Terug
                            </Button>
                        </ButtonGroup>
                        <ProjectStepper step={activeStep} setStep={handleStep} />
                        <Divider sx={{ my: 2, borderColor: 'primary.main', borderWidth: 2 }} />
                        <Button 
                            variant="outlined" 
                            color="primary"
                            onClick={handleStep({ phase: 0, loop: 0, step: "bijlagen" })}
                            sx={{ m: 1 }}
                            disabled={activeStep.step === "bijlagen"}
                            startIcon={<Attachment />}
                        >
                            Bijlagen
                        </Button>
                        <Button 
                            variant="outlined" 
                            color="primary"
                            onClick={handleStep({ phase: 0, loop: 0, step: "voortgang" })}
                            sx={{ m: 1 }}
                            disabled={activeStep.step === "voortgang"}
                            startIcon={<Timeline />}
                        >
                            Voortgang
                        </Button>
                        <Button 
                            variant="outlined" 
                            color="primary"
                            onClick={() => setExportDialogOpen(true)}
                            sx={{ m: 1 }}
                            disabled={activeStep.step === "exporteer"}
                            startIcon={<FileDownload />}
                        >
                            Export
                        </Button>
                    </Stack>
                    <Paper>
                        <Stack justifyContent={"space-between"} direction={"row"}>
                            <Typography variant="h4" component="h1" gutterBottom>
                                {project.name} - {steps[activeStep.phase].steps[activeStep.step] || (activeStep.step === "bijlagen" ? "Bijlagen overzicht" : activeStep.step === "voortgang" ? "Voortgang" : "")}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {/* Delete last loop button - only show if there are design loops and user has permission */}
                                {hasDesignLoops() && currentLoopIsLastLoop(activeStep) && (project.user_is_owner || project.user_is_orgadmin) && (
                                    <Tooltip title="Laatste ontwerpronde verwijderen">
                                        <IconButton
                                            color="error"
                                            onClick={() => setDeleteConfirmDialog(true)}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {/* {lockAndUnlockPage(activeStep)} */}
                            </Stack>
                        </Stack>
                        {renderSwitch(activeStep)}
                    </Paper>
                </Stack>
            ) : null}
        </>
    );
}

ProjectMain.propTypes = {
    project: PropTypes.object.isRequired,
};
